// Stacked monthly renewal-pipeline chart — FE-035 / spec §7.0.6.
//
// Tier 1 ships a vertical-bar layout built from Tailwind primitives;
// each bar segment is a `<Link>` so the click target == the visible
// rectangle. A heavier chart library can replace this in a polish
// pass without changing the section contract.

import { Link } from "react-router-dom";

import {
  buildAccountFilterUrl,
  type RenewalPipelineBucket,
} from "@/features/dashboard";

import {
  RENEWAL_STATUS_META,
  RENEWAL_STATUS_ORDER,
  type RenewalStatusKey,
} from "./statusMeta";


interface RenewalPipelineChartProps {
  buckets: RenewalPipelineBucket[];
}


export function RenewalPipelineChart({
  buckets,
}: RenewalPipelineChartProps) {
  const max = buckets.reduce((m, b) => Math.max(m, b.total), 0);
  return (
    <div
      data-testid="renewal-pipeline-chart"
      role="figure"
      aria-label="Upcoming renewals by month and readiness"
      className="flex flex-col"
    >
      <div className="flex h-48 items-stretch gap-3 overflow-x-auto pb-1">
        {buckets.map((bucket) => (
          <BucketColumn key={bucket.month} bucket={bucket} max={max} />
        ))}
      </div>
    </div>
  );
}


function BucketColumn({
  bucket, max,
}: { bucket: RenewalPipelineBucket; max: number }) {
  const summaryLabel = formatBucketLabel(bucket);
  return (
    <div
      data-testid={`renewal-bucket-${bucket.month}`}
      aria-label={summaryLabel}
      className="flex min-w-[3rem] flex-1 flex-col items-stretch"
    >
      <div className="flex flex-1 items-end">
        {bucket.total === 0 ? (
          <div
            data-testid={`renewal-bucket-empty-${bucket.month}`}
            className="h-full w-full rounded-sm border border-dashed border-border-subtle"
            aria-hidden="true"
          />
        ) : (
          <BucketStack bucket={bucket} max={max} />
        )}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-1">
        <span className="text-xs text-text-tertiary">
          {formatMonthLabel(bucket.month)}
        </span>
        <span
          className="text-xs font-medium tabular-nums text-text-primary"
          data-testid={`renewal-bucket-total-${bucket.month}`}
        >
          {bucket.total}
        </span>
      </div>
    </div>
  );
}


function BucketStack({
  bucket, max,
}: { bucket: RenewalPipelineBucket; max: number }) {
  const heightPct = max === 0 ? 0 : (bucket.total / max) * 100;
  return (
    <div
      className="relative h-full w-full"
      style={{
        height: `${heightPct}%`,
        minHeight: bucket.total > 0 ? "8%" : "0",
      }}
    >
      <div
        className="flex h-full w-full flex-col-reverse overflow-hidden rounded-sm"
        // The chart paints from the bottom up: on_track at the base,
        // not_started at the top so the "needs attention" weight sits
        // visually above the green band.
      >
        {RENEWAL_STATUS_ORDER.map((status) => {
          const value = bucket[status];
          if (value === 0) return null;
          const segmentPct = (value / bucket.total) * 100;
          return (
            <RenewalSegment
              key={status}
              bucket={bucket}
              status={status}
              value={value}
              segmentPct={segmentPct}
            />
          );
        })}
      </div>
    </div>
  );
}


function RenewalSegment({
  bucket, status, value, segmentPct,
}: {
  bucket: RenewalPipelineBucket;
  status: RenewalStatusKey;
  value: number;
  segmentPct: number;
}) {
  const meta = RENEWAL_STATUS_META[status];
  const href = buildAccountFilterUrl({
    renewal_month: bucket.month,
    renewal_status: status,
  });
  return (
    <Link
      to={href}
      data-testid={`renewal-segment-${bucket.month}-${status}`}
      aria-label={
        `${formatMonthLabel(bucket.month)}: ${value} ${meta.label.toLowerCase()}` +
        ` — ${meta.helper}`
      }
      style={{ height: `${segmentPct}%` }}
      className={`block w-full ${meta.swatch} hover:opacity-90`}
    />
  );
}


function formatMonthLabel(month: string): string {
  // ``YYYY-MM`` → e.g. "Jun 2026". Fall back to the raw value if the
  // month string is malformed. Format in UTC so a user east or west
  // of UTC doesn't see the label slip a month (a Date built from
  // ``Date.UTC(year, idx, 1)`` is midnight UTC; rendering in local
  // time can shove it into the prior month for any negative offset).
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const year = Number(match[1]);
  const monthIdx = Number(match[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return month;
  const date = new Date(Date.UTC(year, monthIdx, 1));
  return date.toLocaleDateString(undefined, {
    month: "short", year: "numeric", timeZone: "UTC",
  });
}


function formatBucketLabel(bucket: RenewalPipelineBucket): string {
  if (bucket.total === 0) {
    return `${formatMonthLabel(bucket.month)}: no renewals scheduled`;
  }
  const parts: string[] = [];
  for (const status of RENEWAL_STATUS_ORDER) {
    const value = bucket[status];
    if (value > 0) {
      parts.push(`${value} ${RENEWAL_STATUS_META[status].label.toLowerCase()}`);
    }
  }
  return `${formatMonthLabel(bucket.month)}: ${parts.join(", ")}`;
}
